import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { getApiHeaders } from './config';
import { buildTradeServiceUrl } from './tradeServiceRouting';
import { useWorkspace } from './workspace/WorkspaceContext';

Modal.setAppElement('#root');

function ManualSignalOrderFormBySymbol({ isOpen, closeModal, symbol, timeFrame, side }) {
  const { workspace } = useWorkspace();
  const [formData, setFormData] = useState({
    symbol: symbol || '',
    side: side || '',
    orderType: 'market',
    limitPrice: '',
    confluences: [],
    presetStopLoss: '',
    presetTakeProfit: '',
    srDemandSupplyLevels: '',
    tradingStrategy: 'PRICE_EMA12_CROSS',
    tradingTimeFrame: timeFrame || 0,
    activePositionCountLimitCheck: '',
    exchange: workspace.exchange,
    marketType: workspace.marketType,
  });

  const [submissionResult, setSubmissionResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      exchange: workspace.exchange,
      marketType: workspace.marketType,
      symbol: symbol || prev.symbol,
      side: side || prev.side,
      tradingTimeFrame: timeFrame || prev.tradingTimeFrame,
    }));
  }, [workspace, symbol, side, timeFrame]);

  useEffect(() => {
    const fetchMarketPrice = async () => {
      const response = await fetch(
        buildTradeServiceUrl('/api/bot/get/getMarkPriceBySymbol', workspace, { symbol }),
        { headers: getApiHeaders() }
      );
      const data = await response.json();
      setFormData((prevFormData) => ({
        ...prevFormData,
        limitPrice: data.markPrice,
        symbol,
        side,
      }));
    };

    if (symbol) {
      fetchMarketPrice().catch((err) => setError(err.message));
    }
  }, [side, symbol, workspace]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleConfluencesChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      confluences: e.target.value.split(',').map((item) => item.trim()).filter(Boolean),
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    setLoading(true);

    let tradeUrl;
    try {
      tradeUrl = buildTradeServiceUrl('/api/bot/ui/post/adhoc-order-service', workspace);
    } catch (routingError) {
      setError(routingError.message);
      setLoading(false);
      return;
    }
    fetch(tradeUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getApiHeaders() },
      body: JSON.stringify({
        ...formData,
        exchange: workspace.exchange,
        marketType: workspace.marketType,
      }),
    })
      .then((res) => {
        if (!res.ok) throw new Error('Network response was not ok');
        return res.json();
      })
      .then((data) => {
        setSubmissionResult(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={closeModal}
      contentLabel="Manual Signal Order Form"
      className="ReactModal__Content"
      overlayClassName="ReactModal__Overlay"
    >
      <button className="modal-close-button" onClick={closeModal}>&times;</button>
      <h2 className="page-title">Submit AdHoc Signal Order</h2>
      <h3>[Non Active]</h3>
      <p style={{ marginTop: 0, color: '#666' }}>
        Workspace: <strong>{workspace.exchange}</strong> / <strong>{workspace.marketType}</strong>
      </p>
      {loading && <p className="loading-message">Submitting...</p>}
      {error && <p style={{ color: 'red' }}>Error: {error}</p>}
      {submissionResult && <p>Result: {JSON.stringify(submissionResult)}</p>}

      {!loading && !submissionResult && (
        <form className="modal-form" onSubmit={handleSubmit}>
          <label>
            Symbol:
            <input
              type="text"
              name="symbol"
              value={formData.symbol}
              readOnly
            />
          </label>

          <label>
            Trading Time Frame:
            <input
              type="text"
              name="tradingTimeFrame"
              value={timeFrame}
              readOnly
            />
          </label>

          <label>
            Signal Side:
            <input
              type="text"
              name="side"
              value={formData.side}
              readOnly
            />
          </label>

          <label>Order Type:
            <select name="orderType" value={formData.orderType} onChange={handleInputChange}>
              <option value="limit">Limit</option>
              <option value="market">Market</option>
            </select>
          </label>

          <label>Limit Price:
            <input type="number" step="0.01" name="limitPrice" value={formData.limitPrice} onChange={handleInputChange} />
          </label>

          <label>Confluences (comma-separated):
            <input type="text" value={formData.confluences.join(', ')} onChange={handleConfluencesChange} />
          </label>

          <label>Preset Stop Loss:
            <input type="text" name="presetStopLoss" value={formData.presetStopLoss} onChange={handleInputChange} />
          </label>

          <label>Preset Take Profit:
            <input type="text" name="presetTakeProfit" value={formData.presetTakeProfit} onChange={handleInputChange} />
          </label>

          <label>SR/Demand Supply Levels:
            <input type="text" name="srDemandSupplyLevels" value={formData.srDemandSupplyLevels} onChange={handleInputChange} />
          </label>

          <label>Trading Strategy:
            <select name="tradingStrategy" value={formData.tradingStrategy} onChange={handleInputChange}>
              <option value="PRICE_EMA12_CROSS">PRICE_EMA12_CROSS</option>
              <option value="DEFAULT">DEFAULT</option>
            </select>
          </label>

          <label>Active Positions Count Rule:
            <select name="activePositionCountLimitCheck" value={formData.activePositionCountLimitCheck} onChange={handleInputChange}>
              <option value="REQUIRED">REQUIRED</option>
              <option value="SKIP">SKIP</option>
            </select>
          </label>

          <div className="modal-button-container">
            <button type="submit">Submit</button>
            <button type="button" onClick={closeModal}>Cancel</button>
          </div>
        </form>
      )}
    </Modal>
  );
}

export default ManualSignalOrderFormBySymbol;
